import { t } from '@lingui/core/macro'
import { WandSparkles } from 'lucide-react'
import { createRoot } from 'react-dom/client'
import { sanitizeHtml } from './sanitize'
import { ErrorCorrectionConfidenceLevel } from '@global/api/error-correction/type'
import { Button } from '@components/v2/button'
import { cn } from '@global/utils/tailwind'

const ROW_COUNT_THRESHOLD_FOR_BOTTOM_POSITIONING = 20

const handleMouseDown = (e: React.MouseEvent) => {
  e.stopPropagation()
}

interface ErrorCorrectionTooltipProps {
  cellValue: string
  previousValue: string
  modification: string
  confidence?: ErrorCorrectionConfidenceLevel
  toggleErrorCorrection: () => void
  modificationReverted: boolean
  totalRowCount?: number
}

function getErrorCorrectionConfidenceLabel(confidence?: ErrorCorrectionConfidenceLevel) {
  switch (confidence) {
    case 'LOW':
      return t({
        message: 'Low confidence',
        comment: 'Confidence level label for error correction in tooltip',
      })
    case 'MEDIUM':
      return t({
        message: 'Medium confidence',
        comment: 'Confidence level label for error correction in tooltip',
      })
    case 'HIGH':
      return t({
        message: 'High confidence',
        comment: 'Confidence level label for error correction in tooltip',
      })
    case 'VERY HIGH':
      return t({
        message: 'Very high confidence',
        comment: 'Confidence level label for error correction in tooltip',
      })
  }
}

const DisplayValue = ({ value, maxLength = 24 }: { value: string; maxLength?: number }) => {
  const isTruncated = value && value.length > maxLength
  const truncatedValue = isTruncated ? `${value.substring(0, maxLength)}...` : value

  return <span title={isTruncated ? value : undefined}>{truncatedValue}</span>
}

const DisplayBeforeAfter = ({
  previousValue,
  modification,
  modificationReverted,
}: {
  previousValue: string
  modification: string
  modificationReverted: boolean
}) => {
  return (
    <div className="mb-5">
      {modificationReverted ? (
        <>
          <DisplayValue value={modification} />
          {' → '}
          <DisplayValue value={previousValue || t`Blank`} />
        </>
      ) : (
        <>
          <DisplayValue value={previousValue || t`Blank`} />
          {' → '}
          <DisplayValue value={modification} />
        </>
      )}
    </div>
  )
}

const ErrorCorrectionTooltip = ({
  cellValue,
  previousValue,
  modification,
  confidence,
  toggleErrorCorrection,
  modificationReverted,
  totalRowCount,
}: ErrorCorrectionTooltipProps) => {
  const confidenceLabel = getErrorCorrectionConfidenceLabel(confidence)
  const shouldInvertPosition = (totalRowCount ?? 0) >= ROW_COUNT_THRESHOLD_FOR_BOTTOM_POSITIONING

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(cellValue) }} />
      <div className="tooltipWrapper" onMouseDown={handleMouseDown}>
        <div className={cn('error-correction-tooltip', { 'has-many-rows': shouldInvertPosition })}>
          <div className="bg-white border border-gray-200 shadow-lg rounded">
            <div className="flex items-center gap-3 px-[14px] py-[8px] bg-cv-ai-purple-light font-medium">
              <WandSparkles className="text-cv-ai-purple" size={18} strokeWidth={1.5} />
              <h6>{t`Smart Correct`}</h6>
            </div>

            <div className="p-4 pt-3 pb-2">
              {modificationReverted && <div className="italic mb-3">{t`Correction reverted`}</div>}

              <DisplayBeforeAfter
                previousValue={previousValue}
                modification={modification}
                modificationReverted={modificationReverted}
              />

              <div className="flex justify-between items-center mb-3">
                {previousValue && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleErrorCorrection}
                    className="font-normal text-cv-slate-700"
                  >
                    {modificationReverted ? t`Undo` : t`Revert change`}
                  </Button>
                )}

                {!modificationReverted && !!confidenceLabel && (
                  <div className="text-xs text-cv-slate-600">{confidenceLabel}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const renderErrorCorrectionTooltip = (
  td: HTMLTableColElement,
  cellValue: string,
  errorCorrection: {
    previousValue: string
    modification: string
    confidence?: ErrorCorrectionConfidenceLevel
    modificationReverted: boolean
  },
  toggleErrorCorrection: () => void,
  totalRowCount?: number,
) => {
  const root = createRoot(td)
  root.render(
    <ErrorCorrectionTooltip
      cellValue={cellValue}
      toggleErrorCorrection={toggleErrorCorrection}
      totalRowCount={totalRowCount}
      {...errorCorrection}
    />,
  )
}
